import { Formik } from "formik";
import { useEffect, useState } from "react";
import HireTalentLayout from "../../layouts/HireTalentLayout";
import { getProfile, getToken, setProfile } from "../../utils/pip";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { hireTalentPaths } from "../../routes/hire-talent";
import {
  fetchHireTalentProfile,
  updateHireTalentProfileStep,
} from "../../redux/features/hire-talent/actions/authAction";
import { normalizeHireTalentProfile } from "../../utils/hireTalentProfile";
import Loader from "../../components/loader";
import ErrorMessage from "../../components/form/ErrorMessage";

const EditProfile = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { subLoading } = useSelector((state) => state.hireTalent?.auth);
  const [profile, setProfileState] = useState(() => getProfile("hire-talent") || {});

  const industryOptions = [
    "Information Technology (IT) & Software",
    "Finance & Banking",
    "Healthcare & Pharmaceuticals",
    "Education & Training",
    "Engineering & Manufacturing",
    "Retail & E-commerce",
    "Hospitality & Tourism",
    "Marketing, Media & Communications",
    "Transportation & Logistics",
    "Energy & Utilities",
    "Legal Services",
    "Agriculture & Food Industry",
    "Government & Public Sector",
    "Nonprofit & NGOs",
  ];

  const companySizeOptions = [
    "Startup (1–10 employees)",
    "Growing Company (11–50 employees)",
    "Scaling Company (51–200 employees)",
    "Large Company (201–500 employees)",
    "Enterprise (500+ employees)",
  ];

  const countryOptions = ["USA", "India", "United Kingdom", "Canada", "Australia", "UAE"];

  const talentTypeOptions = [
    "Graphic Designer",
    "Web Developer",
    "Mobile App",
    "Marketing Specialist",
    "Copywriter",
    "Data Analyst",
    "Video Editor",
    "UI/UX Designer",
    "Other",
  ];

  const engagementTypeOptions = [
    "Freelance project",
    "Part-time contract",
    "Long-term contract",
    "Full-time remote",
  ];
  const timezoneOptions = ["UTC", "GMT", "PST", "MST", "CST", "EST", "IST"];

  const communicationToolsOptions = [
    "Slack",
    "Microsoft Teams",
    "Zoom",
    "Google Meet",
    "Other",
  ];

  const linkRegex =
    /^(https?:\/\/)?((localhost|(\d{1,3}\.){3}\d{1,3})|([\w-]+\.)+[a-zA-Z]{2,})(:\d+)?(\/[^\s]*)?$/;
  const isValidLink = (value) => linkRegex.test(value);

  const mapEmployeesToCompanySize = (value = "") => {
    if (!value) return "";
    const cleaned = String(value).trim();
    const map = {
      "1-10": "Startup (1–10 employees)",
      "11-50": "Growing Company (11–50 employees)",
      "51-200": "Scaling Company (51–200 employees)",
      "201-500": "Large Company (201–500 employees)",
      "500+": "Enterprise (500+ employees)",
    };
    return map[cleaned] || cleaned;
  };

  const mapCompanySizeToEmployees = (value = "") => {
    if (!value) return "";
    const numbers = value.match(/\d+/g) || [];
    if (value.includes("+") && numbers[0]) return `${numbers[0]}+`;
    if (numbers.length >= 2) return `${numbers[0]}-${numbers[1]}`;
    return value;
  };

  const initialValues = {
    profile_image: profile?.profile_image || "",
    profile_image_file: null,
    full_name: profile?.full_name || "",
    email: profile?.email || "",
    password: profile?.password || "",
    company_name: profile?.company_name || "",
    industry: profile?.industry || "",
    company_description: profile?.company_description || profile?.company_bio || "",
    company_size: mapEmployeesToCompanySize(
      profile?.company_size || profile?.number_of_employees || ""
    ),
    company_website: profile?.company_website || "",
    linkedin_company: profile?.linkedin_company || profile?.linkedin_company_page || "",
    country: profile?.country || "",
    city: profile?.city || "",
    primary_contact_name:
      profile?.primary_contact_name || profile?.primary_contact_full_name || "",
    primary_contact_title:
      profile?.primary_contact_title || profile?.primary_contact_job_title || "",
    primary_contact_email:
      profile?.primary_contact_email || profile?.primary_contact_work_email || "",
    primary_contact_phone:
      profile?.primary_contact_phone || profile?.primary_contact_phone_number || "",
    secondary_contact_name:
      profile?.secondary_contact_name || profile?.secondary_contact_full_name || "",
    secondary_contact_title:
      profile?.secondary_contact_title || profile?.secondary_contact_job_title || "",
    secondary_contact_email:
      profile?.secondary_contact_email || profile?.secondary_contact_work_email || "",
    secondary_contact_phone:
      profile?.secondary_contact_phone || profile?.secondary_contact_phone_number || "",
    talent_types: profile?.talent_types || [],
    talent_other: profile?.talent_other || "",
    engagement_types: profile?.engagement_types || [],
    work_environment: profile?.work_environment || "",
    preferred_timezone: profile?.preferred_timezone || "",
    communication_tools: profile?.communication_tools || [],
    communication_other: profile?.communication_other || "",
    collaboration_type: profile?.collaboration_type || "",
    consent_accuracy: profile?.consent_accuracy || false,
    consent_contact: profile?.consent_contact || false,
    rating: profile?.rating || "4.7",
    projects_posted: profile?.projects_posted || "18",
  };
  useEffect(() => {
    const token = getToken("hire-talent");
    if (!token) return;
    dispatch(fetchHireTalentProfile()).then((resp) => {
      const data = resp?.payload?.data;
      if (!data) return;
      const normalized = normalizeHireTalentProfile({
        ...data,
        ...(data.hiretalent || {}),
      });
      setProfile("hire-talent", normalized);
      setProfileState(normalized);
    });
  }, [dispatch]);

  const buildHiringNeeds = (values) => {
    const items = (values.talent_types || []).filter((item) => item !== "Other");
    if (values.talent_other?.trim()) items.push(values.talent_other.trim());
    return [...new Set(items)].join(", ");
  };

  const buildEngagementType = (values) => {
    return (values.engagement_types || []).join(", ");
  };

  const buildCommunicationTools = (values) => {
    const items = (values.communication_tools || []).filter((item) => item !== "Other");
    if (values.communication_other?.trim()) items.push(values.communication_other.trim());
    return items.join(", ");
  };

  const handleSave = (values) => {
    const formData = new FormData();
    if (values.profile_image_file) {
      formData.append("profile_image", values.profile_image_file);
    }
    formData.append("full_name", values.full_name || "");
    formData.append("company_name", values.company_name || "");
    formData.append("industry", values.industry || "");
    formData.append("company_website", values.company_website || "");
    formData.append(
      "number_of_employees",
      mapCompanySizeToEmployees(values.company_size || "")
    );
    formData.append("country", values.country || "");
    formData.append("city", values.city || "");
    formData.append("company_bio", values.company_description || "");
    formData.append("linkedin_company_page", values.linkedin_company || "");
    formData.append("primary_contact_full_name", values.primary_contact_name || "");
    formData.append("primary_contact_job_title", values.primary_contact_title || "");
    formData.append("primary_contact_work_email", values.primary_contact_email || "");
    formData.append("primary_contact_phone_number", values.primary_contact_phone || "");
    formData.append("secondary_contact_full_name", values.secondary_contact_name || "");
    formData.append("secondary_contact_job_title", values.secondary_contact_title || "");
    formData.append("secondary_contact_work_email", values.secondary_contact_email || "");
    formData.append("secondary_contact_phone_number", values.secondary_contact_phone || "");
    formData.append("hiring_needs", buildHiringNeeds(values));
    formData.append("engagement_type", buildEngagementType(values));
    formData.append("work_enviroment", values.work_environment || "");
    formData.append("preferred_timezone", values.preferred_timezone || "");
    formData.append("communication_tools_used", buildCommunicationTools(values));
    formData.append("types_of_collaboration", values.collaboration_type || "");
    formData.append("information_confirmed", values.consent_accuracy ? "true" : "false");
    formData.append("agree_contact_opportunities", values.consent_contact ? "true" : "false");

    const callback = (res) => {
      if (!res?.success) return;
      dispatch(fetchHireTalentProfile({ force: true })).then((resp) => {
        const data = resp?.payload?.data;
        if (data) {
          const normalized = normalizeHireTalentProfile({
            ...profile,
            ...data,
            ...(data?.hiretalent || {}),
          });
          setProfile("hire-talent", normalized);
        }
        navigate(hireTalentPaths.Profile);
      });
    };

    dispatch(updateHireTalentProfileStep({ payload: formData, callback }));
  };

  return (
    <HireTalentLayout>
      {subLoading && <Loader />}
      <div className="ct_px_40 my-5">
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div>
            <h4 className="ct_fs_20_new ct_fw_600 ct_darkblue_text">Edit Company Profile</h4>
            <p className="mb-0 ct_grey_text">Update your company information</p>
          </div>
          <button
            className="ct_yellow_btn ct_outline_grey ct_h_44"
            type="button"
            onClick={() => navigate(hireTalentPaths.Profile)}
          >
            Back to Profile
          </button>
        </div>

        <Formik
          initialValues={initialValues}
          validate={(values) => {
            const errors = {};
            const companyWebsite = (values.company_website || "").trim();
            if (companyWebsite && !isValidLink(companyWebsite)) {
              errors.company_website = "Please enter a valid link";
            }
            const linkedinCompany = (values.linkedin_company || "").trim();
            if (linkedinCompany && !isValidLink(linkedinCompany)) {
              errors.linkedin_company = "Please enter a valid link";
            }
            return errors;
          }}
          onSubmit={handleSave}
          enableReinitialize
        >
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue }) => (
            <form onSubmit={handleSubmit}>
              <div className="ct_outline_bg ct_border_radius_10 ct_p_20">
                <p className="ct_fs_16 mb-3 ct_darkblue_text">Company Logo</p>
                <div className="d-flex align-items-center gap-3 ct_flex_col_575 mb-4">
                  <div className="ct_profile_pic ct_flex_shrink_0">
                    <img
                      src={values.profile_image || "/assets/img/user_profile.png"}
                      alt="Company Logo"
                    />
                  </div>
                  <div className="ct_flex_1">
                    <label className="ct_upload_btn">
                      Upload Company Logo
                      <input
                        className="ct_file_input"
                        type="file"
                        accept="image/*"
                        onChange={(event) => {
                          const file = event.currentTarget.files?.[0];
                          if (file) {
                            const previewUrl = URL.createObjectURL(file);
                            setFieldValue("profile_image", previewUrl);
                            setFieldValue("profile_image_file", file);
                          }
                        }}
                      />
                    </label>
                    <p className="ct_fs_12 ct_grey_text mb-0">
                      Upload a company logo.
                    </p>
                  </div>
                </div>
              </div>

              <div className="ct_outline_bg ct_border_radius_10 ct_p_20 ct_mt_40">
                <p className="ct_fs_16 mb-3 ct_darkblue_text">Company Information</p>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                        Company Name
                      </label>
                      <input
                        type="text"
                        className="form-control ct_input"
                        id="company_name"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.company_name}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">Industry</label>
                      <div className="d-flex flex-column gap-2 position-relative">
                        <select
                          className="form-control ct_input"
                          id="industry"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.industry}
                        >
                          <option value="">Select Industry</option>
                          {industryOptions.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                        <i class="fa-solid fa-angle-down ct_show_eye ct_pointer_events_none"></i>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                        Company Size
                      </label>
                      <div className="d-flex flex-column gap-2 position-relative">
                        <select
                          className="form-control ct_input"
                          id="company_size"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.company_size}
                        >
                          <option value="">Select Size</option>
                          {companySizeOptions.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                        <i class="fa-solid fa-angle-down ct_show_eye ct_pointer_events_none"></i>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                        Company Website
                      </label>
                      <input
                        type="text"
                        className="form-control ct_input"
                        id="company_website"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.company_website}
                      />
                      <ErrorMessage
                        errors={errors}
                        touched={touched}
                        fieldName="company_website"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                        LinkedIn Company Page
                      </label>
                      <input
                        type="text"
                        className="form-control ct_input"
                        id="linkedin_company"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.linkedin_company}
                      />
                      <ErrorMessage
                        errors={errors}
                        touched={touched}
                        fieldName="linkedin_company"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">Country</label>
                      <div className="d-flex flex-column gap-2 position-relative">
                        <select
                          className="form-control ct_input"
                          id="country"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.country}
                        >
                          <option value="">Select Country</option>
                          {countryOptions.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                        <i class="fa-solid fa-angle-down ct_show_eye ct_pointer_events_none"></i>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">City</label>
                      <input
                        type="text"
                        className="form-control ct_input"
                        id="city"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.city}
                      />
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="form-group mb-0">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                        Company Description
                      </label>
                      <textarea
                        rows="4"
                        className="form-control ct_input h-auto"
                        id="company_description"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.company_description}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="ct_outline_bg ct_border_radius_10 ct_p_20 ct_mt_40">
                <p className="ct_fs_16 mb-3 ct_darkblue_text">Contact Persons</p>
                <div className="row">
                  <div className="col-12">
                    <p className="ct_fs_14 ct_grey_text mb-3">Primary Contact</p>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                        Full Name
                      </label>
                      <input
                        type="text"
                        className="form-control ct_input"
                        id="primary_contact_name"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.primary_contact_name}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                        Job Title
                      </label>
                      <input
                        type="text"
                        className="form-control ct_input"
                        id="primary_contact_title"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.primary_contact_title}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                        Work Email
                      </label>
                      <input
                        type="email"
                        className="form-control ct_input"
                        id="primary_contact_email"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.primary_contact_email}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                        Phone Number
                      </label>
                      <input
                        type="text"
                        className="form-control ct_input"
                        id="primary_contact_phone"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.primary_contact_phone}
                      />
                    </div>
                  </div>
                  <div className="col-12">
                    <p className="ct_fs_14 ct_grey_text mb-3">Secondary Contact</p>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                        Full Name
                      </label>
                      <input
                        type="text"
                        className="form-control ct_input"
                        id="secondary_contact_name"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.secondary_contact_name}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                        Job Title
                      </label>
                      <input
                        type="text"
                        className="form-control ct_input"
                        id="secondary_contact_title"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.secondary_contact_title}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                        Work Email
                      </label>
                      <input
                        type="email"
                        className="form-control ct_input"
                        id="secondary_contact_email"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.secondary_contact_email}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                        Phone Number
                      </label>
                      <input
                        type="text"
                        className="form-control ct_input"
                        id="secondary_contact_phone"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.secondary_contact_phone}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="ct_outline_bg ct_border_radius_10 ct_p_20 ct_mt_40">
                <p className="ct_fs_16 mb-3 ct_darkblue_text">Hiring Needs</p>
                <div className="row">
                  <div className="col-12">
                    <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                      Talent Types
                    </label>
                    <div className="d-flex flex-wrap gap-3 mb-4">
                      {talentTypeOptions.map((option) => (
                        <label key={option} className="d-flex align-items-center gap-2">
                          <input
                            type="checkbox"
                            checked={values.talent_types.includes(option)}
                            onChange={(event) => {
                              if (event.target.checked) {
                                setFieldValue("talent_types", [
                                  ...values.talent_types,
                                  option,
                                ]);
                              } else {
                                setFieldValue(
                                  "talent_types",
                                  values.talent_types.filter((item) => item !== option)
                                );
                              }
                            }}
                          />
                          <span className="ct_fs_14 ct_darkblue_text">{option}</span>
                        </label>
                      ))}
                    </div>
                    {values.talent_types.includes("Other") && (
                      <div className="form-group mb-4">
                        <input
                          type="text"
                          className="form-control ct_input"
                          placeholder="Other, please specify"
                          id="talent_other"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.talent_other}
                        />
                      </div>
                    )}
                  </div>
                  <div className="col-12">
                    <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                      Engagement Type
                    </label>
                    <div className="d-flex flex-wrap gap-3 mb-4">
                      {engagementTypeOptions.map((option) => (
                        <label key={option} className="d-flex align-items-center gap-2">
                          <input
                            type="checkbox"
                            checked={values.engagement_types.includes(option)}
                            onChange={(event) => {
                              if (event.target.checked) {
                                setFieldValue("engagement_types", [
                                  ...values.engagement_types,
                                  option,
                                ]);
                              } else {
                                setFieldValue(
                                  "engagement_types",
                                  values.engagement_types.filter((item) => item !== option)
                                );
                              }
                            }}
                          />
                          <span className="ct_fs_14 ct_darkblue_text">{option}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="ct_outline_bg ct_border_radius_10 ct_p_20 ct_mt_40">
                <p className="ct_fs_16 mb-3 ct_darkblue_text">Work Environment</p>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                        Preferred Time Zone
                      </label>
                      <div className="d-flex flex-column gap-2 position-relative">
                        <select
                          className="form-control ct_input"
                          id="preferred_timezone"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.preferred_timezone}
                        >
                          <option value="">Select Time Zone</option>
                          {timezoneOptions.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                        <i class="fa-solid fa-angle-down ct_show_eye ct_pointer_events_none"></i>
                      </div>
                    </div>
                  </div>
                  <div className="col-12">
                    <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                      Communication Tools Used
                    </label>
                    <div className="d-flex flex-wrap gap-3 mb-4">
                      {communicationToolsOptions.map((option) => (
                        <label key={option} className="d-flex align-items-center gap-2">
                          <input
                            type="checkbox"
                            checked={values.communication_tools.includes(option)}
                            onChange={(event) => {
                              if (event.target.checked) {
                                setFieldValue("communication_tools", [
                                  ...values.communication_tools,
                                  option,
                                ]);
                              } else {
                                setFieldValue(
                                  "communication_tools",
                                  values.communication_tools.filter((item) => item !== option)
                                );
                              }
                            }}
                          />
                          <span className="ct_fs_14 ct_darkblue_text">{option}</span>
                        </label>
                      ))}
                    </div>
                    {values.communication_tools.includes("Other") && (
                      <div className="form-group mb-4">
                        <input
                          type="text"
                          className="form-control ct_input"
                          placeholder="Other, please specify"
                          id="communication_other"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.communication_other}
                        />
                      </div>
                    )}
                  </div>
                  <div className="col-12">
                    <div className="form-group mb-0">
                      <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                        Collaboration Preference
                      </label>
                      <textarea
                        rows="4"
                        className="form-control ct_input h-auto"
                        id="collaboration_type"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.collaboration_type}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Consent section hidden on edit profile */}

              <div className="d-flex justify-content-end gap-3 ct_mt_40">
                <button
                  type="button"
                  className="ct_outline_btn ct_h_44"
                  onClick={() => navigate(hireTalentPaths.Profile)}
                >
                  Cancel
                </button>
                <button type="submit" className="ct_yellow_btn ct_h_44">
                  Save Changes
                </button>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </HireTalentLayout>
  );
};

export default EditProfile;

